import { useState, useEffect, useRef, useCallback } from "react";
import {
  StoreIcon,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit2,
  X,
  Save,
  Upload,
  Plus,
  Minus,
  TrendingUp,
  Bell,
  History,
  ChevronDown,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import { ALERT_STATUS, showAlert } from "../../components/Modals/alert";
import { Product, ProductVariation } from "../../types/Product";
import LoadingComponent from "../../components/LoadingComponent";
import { useDebounce } from "../../hooks/useDebounce";
import { Tooltip } from "@mui/material";
import Pagination from "../../components/Pagination";
import { LocalizedText } from "../../types/LocalizedText";
import { useCurrency } from "../../components/Currency/CurrencyContext";
import { useAuth } from "../../context/AuthContext";
import LowStockAlertEditor from "../../components/Inventory/LowStockAlertEditor";

interface InventoryItem {
  id: string;
  sku: string;
  name: LocalizedText;
  price: string;
  costPrice: string;
  stock: number;
  lowStockThreshold: number;
  images?: string[];
  category: {
    id: string;
    name: LocalizedText;
    slug: string;
  };
  stockStatus: "in_stock" | "low_stock" | "out_of_stock";
  stockColor: string;
  stockValue: number;
}

interface InventoryTransaction {
  id: string;
  productId: string;
  productName: string;
  type: "in" | "out" | "adjustment";
  quantity: number;
  previousStock: number;
  newStock: number;
  reference: string;
  referenceId: string;
  reason: string;
  createdAt: string;
  notes: string;
  performedById: string;
  updatedAt: string;
  deletedAt: string;
  product: Product;
  performer: any;
}

interface StockApprovalRequest {
  id: string;
  status?: "pending" | "approved" | "rejected" | string;
  notes?: string | null;
  reviewNotes?: string | null;
  currentStock?: number | null;
  previousStock?: number | null;
  requestedStock?: number | null;
  proposedStock?: number | null;
  newStock?: number | null;
  stock?: number | null;
  createdAt?: string;
  updatedAt?: string;
  reviewedAt?: string | null;
  requester?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  reviewer?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  product?: {
    id: string;
    sku?: string;
    name?: LocalizedText | string;
    images?: string[];
  } | null;
  productName?: LocalizedText | string;
  sku?: string;
}

type InventoryVariation = ProductVariation & {
  _id?: string;
  isActive?: boolean;
};

const InventoryManagementPage = () => {
  const { success, err } = useToast();
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [stockFilter, setStockFilter] = useState<string>("all");
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"name" | "stock">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filtered, setFiltered] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const latestRequestRef = useRef(0);
  const [activeTab, setActiveTab] = useState<"inventory" | "stock_approvals">(
    "inventory",
  );
  const [editModal, setEditModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [transactionsModal, setTransactionsModal] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkUpdates, setBulkUpdates] = useState<Map<string, number>>(
    new Map(),
  );
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsHasMore, setTransactionsHasMore] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsInitialLoading, setTransactionsInitialLoading] =
    useState(false);
  const [stockApprovals, setStockApprovals] = useState<StockApprovalRequest[]>(
    [],
  );
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsCurrentPage, setApprovalsCurrentPage] = useState(1);
  const [approvalsItemsPerPage, setApprovalsItemsPerPage] = useState(10);
  const [approvalsTotalItems, setApprovalsTotalItems] = useState(0);
  const [reviewModal, setReviewModal] = useState(false);
  const [selectedApproval, setSelectedApproval] =
    useState<StockApprovalRequest | null>(null);
  const [approvalDecision, setApprovalDecision] = useState<
    "approved" | "rejected"
  >("approved");
  const [approvalNotes, setApprovalNotes] = useState("");
  const [reviewingApproval, setReviewingApproval] = useState(false);
  const transactionsPageSize = 20;
  const transactionsScrollRef = useRef<HTMLDivElement | null>(null);
  const transactionsObserverRef = useRef<HTMLDivElement | null>(null);
  const isFetchingTransactionsRef = useRef(false);

  const [variationsData, setVariationsData] = useState<any[]>([]);
  const [variationStockEdits, setVariationStockEdits] = useState<
    Map<string, number>
  >(new Map());
  const [savingVariations, setSavingVariations] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedVariations, setExpandedVariations] = useState<
    Record<string, InventoryVariation[]>
  >({});
  const [expandedVariationStockEdits, setExpandedVariationStockEdits] =
    useState<Record<string, Map<string, number>>>({});
  const [loadingVariationItems, setLoadingVariationItems] = useState<
    Set<string>
  >(new Set());
  const [savingVariationId, setSavingVariationId] = useState<string | null>(
    null,
  );
  const [editingVariationStockIds, setEditingVariationStockIds] = useState<
    Set<string>
  >(new Set());

  const stockStatuses = [
    {
      value: "in_stock",
      label: t("In Stock"),
      color: "bg-green-100 text-green-800",
      icon: CheckCircle,
    },
    {
      value: "low_stock",
      label: t("Low Stock"),
      color: "bg-yellow-100 text-yellow-800",
      icon: AlertTriangle,
    },
    {
      value: "out_of_stock",
      label: t("Out of Stock"),
      color: "bg-red-100 text-red-800",
      icon: XCircle,
    },
  ];

  useEffect(() => {
    fetchInventory();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    stockFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, stockFilter]);

  const fetchStockApprovals = useCallback(async () => {
    try {
      setApprovalsLoading(true);

      const params = new URLSearchParams({
        page: approvalsCurrentPage.toString(),
        limit: approvalsItemsPerPage.toString(),
      });

      const res = await BaseApi.get(
        `/inventory/stock-approvals/list?${params.toString()}`,
      );

      if (res.success) {
        const safeData = Array.isArray(res.data) ? res.data : [];
        setStockApprovals(safeData);
        setApprovalsTotalItems(res.pagination?.totalItems || safeData.length);
      }
    } catch (error) {
      console.error("Error fetching stock approvals:", error);
      err(t("Failed to load stock approval requests"));
    } finally {
      setApprovalsLoading(false);
    }
  }, [approvalsCurrentPage, approvalsItemsPerPage, err, t]);

  useEffect(() => {
    fetchStockApprovals();
  }, [fetchStockApprovals]);

  // GET /inventory - Get inventory list with current stock levels
  const fetchInventory = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (stockFilter !== "all") {
        params.append("stockLevel", stockFilter);
      }

      const res = await BaseApi.get(`/inventory?${params.toString()}`);
      // Ignore responses from requests superseded by a newer one
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        // ✅ Ensure data has proper structure
        const safeData = (res.data || []).map((item: any) => ({
          ...item,
          images: item.images || [], // Ensure images is always an array
          name: item.name || { en: item.sku, ar: item.sku }, // Fallback name
        }));

        setInventory(safeData);
        setFiltered(safeData);
        setApiTotalItems(res.pagination?.totalItems || safeData.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching inventory:", error);
      err(t("Failed to load inventory"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  // GET /inventory/{id} - Get inventory details for a specific product
  const fetchInventoryDetails = async (itemId: string) => {
    try {
      const res = await BaseApi.get(`/inventory/${itemId}`);
      if (res.success) {
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching inventory details:", error);
    }
    return null;
  };

  // GET /inventory/transactions - Get all inventory transactions
  const fetchTransactions = useCallback(
    async (page = 1, replace = false) => {
      if (isFetchingTransactionsRef.current) {
        return;
      }

      try {
        isFetchingTransactionsRef.current = true;
        setTransactionsLoading(true);
        if (replace) {
          setTransactionsInitialLoading(true);
        }

        const res = await BaseApi.get(
          `/inventory/transactions/history?page=${page}&limit=${transactionsPageSize}`,
        );

        if (res.success) {
          const nextTransactions = res.data || [];

          setTransactions((prev) => {
            if (replace) {
              return nextTransactions;
            }

            const existingIds = new Set(
              prev.map((transaction) => transaction.id),
            );
            const uniqueTransactions = nextTransactions.filter(
              (transaction: InventoryTransaction) =>
                !existingIds.has(transaction.id),
            );
            return [...prev, ...uniqueTransactions];
          });

          setTransactionsPage(page);
          setTransactionsHasMore(
            res.pagination?.hasNextPage ??
              nextTransactions.length === transactionsPageSize,
          );
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        isFetchingTransactionsRef.current = false;
        setTransactionsLoading(false);
        setTransactionsInitialLoading(false);
      }
    },
    [transactionsPageSize],
  );

  const openTransactionsModal = async () => {
    setTransactions([]);
    setTransactionsPage(1);
    setTransactionsHasMore(true);
    setTransactionsModal(true);
    await fetchTransactions(1, true);
  };

  useEffect(() => {
    if (!transactionsModal || !transactionsHasMore) {
      return;
    }

    const root = transactionsScrollRef.current;
    const target = transactionsObserverRef.current;

    if (!root || !target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (
          entry.isIntersecting &&
          !transactionsLoading &&
          transactionsHasMore
        ) {
          fetchTransactions(transactionsPage + 1, false);
        }
      },
      {
        root,
        rootMargin: "160px",
        threshold: 0.1,
      },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [
    transactionsModal,
    transactionsHasMore,
    transactionsLoading,
    transactionsPage,
    fetchTransactions,
  ]);

  // GET /inventory/valuation - Get inventory valuation report
  const fetchValuation = async () => {
    try {
      const res = await BaseApi.get("/inventory/valuation/report");
      if (res.success) {
        // Display valuation data
        const valuationData = res.summary;
        showAlert(
          ALERT_STATUS.WARNING,
          "Valuation Information",
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Total Inventory Cost:{" "}
              <strong>
                {" "}
                {currency.symbol}
                {valuationData.totalCost}
              </strong>
            </li>
            <li>
              Total Price:{" "}
              <strong>
                {currency.symbol}
                {valuationData.totalPrice}
              </strong>
            </li>
            <li>
              Potential Profit:{" "}
              <strong>
                {currency.symbol}
                {valuationData.potentialProfit}
              </strong>
            </li>
          </ul>,
        );
      }
    } catch (error) {
      console.error("Error fetching valuation:", error);
      err(t("Failed to load valuation report"));
    }
  };

  // POST /inventory/{id}/adjust - Adjust inventory stock
  const handleUpdateStock = async (
    itemId: string,
    newStock: number,
    reason?: string,
  ) => {
    try {
      setSaving(true);
      const currentItem = inventory.find((i) => i.id === itemId);
      const quantityChange = currentItem
        ? newStock - currentItem.stock
        : newStock;

      const res = await BaseApi.post(`/inventory/${itemId}/adjust`, {
        quantity: Math.abs(quantityChange),
        // stock: newStock,
        type: quantityChange >= 0 ? "in" : "out",
        notes: reason || "Manual adjustment",
      });

      if (res.success) {
        setInventory(
          inventory.map((item) =>
            item.id === itemId ? { ...item, stock: newStock } : item,
          ),
        );
        if (editingItem?.id === itemId) {
          setEditingItem({ ...editingItem, stock: newStock });
        }
        success(t("Stock updated successfully!"));
        setEditModal(false);
        setEditingItem(null);
        fetchInventory();
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      err(t("Failed to update stock"));
    } finally {
      setSaving(false);
    }
  };

  // POST /inventory/bulk-adjust - Bulk adjust inventory for multiple products
  const handleBulkUpdate = async () => {
    try {
      setSaving(true);
      const adjustments = Array.from(bulkUpdates.entries()).map(
        ([id, newStock]) => {
          const item = inventory.find((i) => i.id === id);
          const quantityChange = item ? newStock - item.stock : newStock;

          return {
            productId: id,
            stock: newStock,
            quantity: Math.abs(quantityChange),
            type: quantityChange >= 0 ? "in" : "out",
            reason: "Bulk adjustment",
          };
        },
      );

      const res = await BaseApi.post("/inventory/bulk-adjust", {
        adjustments,
      });

      if (res.success) {
        success(
          t("Successfully updated") + ` ${adjustments.length} ` + t("items!"),
        );
        setBulkModal(false);
        setSelectedItems(new Set());
        setBulkUpdates(new Map());
        fetchInventory();
      }
    } catch (error) {
      console.error("Error bulk updating:", error);
      err(t("Failed to bulk update"));
    } finally {
      setSaving(false);
    }
  };

  // POST /inventory/send-low-stock-alerts - Manually send low stock alerts
  const handleSendLowStockAlerts = async () => {
    try {
      setSendingAlerts(true);
      const res = await BaseApi.post("/inventory/send-low-stock-alerts", {});

      if (res.success) {
        success(t("Low stock alerts sent successfully!"));
      }
    } catch (error) {
      console.error("Error sending alerts:", error);
      err(t("Failed to send low stock alerts"));
    } finally {
      setSendingAlerts(false);
    }
  };

  const headerActions = [
    {
      label: t("Transactions"),
      icon: History,
      onClick: openTransactionsModal,
      className: "bg-[#0d223f] text-white hover:bg-[#16345d]",
      disabled: false,
    },
    {
      label: t("Valuation"),
      icon: TrendingUp,
      onClick: fetchValuation,
      className: "bg-green-600 text-white hover:bg-green-700",
      disabled: false,
    },
    {
      label: sendingAlerts ? t("Sending...") : t("Send Alerts"),
      icon: Bell,
      onClick: handleSendLowStockAlerts,
      className: "bg-orange-600 text-white hover:bg-orange-700",
      disabled: sendingAlerts,
    },
    {
      label: t("Bulk Update"),
      icon: Upload,
      onClick: () => setBulkModal(true),
      className: "bg-black text-white hover:bg-gray-700",
      disabled: false,
    },
  ];

  const getVariationId = (variation: InventoryVariation | any) =>
    variation?.id || variation?._id;

  const formatVariationAttributes = (
    attributes?: Record<string, unknown> | null,
  ) => {
    const label = Object.entries(attributes || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(" · ");

    return label || t("Default Variation");
  };

  const fetchProductVariations = async (productId: string) => {
    const productRes = await BaseApi.get(`/products/${productId}`);
    return (productRes?.data?.variations ||
      productRes?.data?.product?.variations ||
      []) as InventoryVariation[];
  };

  const toggleVariationRows = async (item: InventoryItem) => {
    if (expandedItems.has(item.id)) {
      setExpandedItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      return;
    }

    setExpandedItems((prev) => new Set(prev).add(item.id));

    if (expandedVariations[item.id]) {
      return;
    }

    setLoadingVariationItems((prev) => new Set(prev).add(item.id));

    try {
      const variations = await fetchProductVariations(item.id);
      setExpandedVariations((prev) => ({
        ...prev,
        [item.id]: variations,
      }));

      const editsMap = new Map<string, number>();
      variations.forEach((variation) => {
        const variationId = getVariationId(variation);
        if (variationId) {
          editsMap.set(variationId, variation.stock ?? 0);
        }
      });

      setExpandedVariationStockEdits((prev) => ({
        ...prev,
        [item.id]: editsMap,
      }));
    } catch (error) {
      console.error("Error fetching product variations:", error);
      err(t("Failed to load variation stock"));
      setExpandedVariations((prev) => ({
        ...prev,
        [item.id]: [],
      }));
    } finally {
      setLoadingVariationItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleExpandedVariationStockChange = (
    productId: string,
    variationId: string,
    stock: number,
  ) => {
    setExpandedVariationStockEdits((prev) => {
      const nextMap = new Map(prev[productId] || []);
      nextMap.set(variationId, Math.max(0, stock));
      return {
        ...prev,
        [productId]: nextMap,
      };
    });
  };

  const getExpandedVariationStockEditKey = (
    productId: string,
    variationId: string,
  ) => `${productId}-${variationId}`;

  const handleStartExpandedVariationStockEdit = (
    productId: string,
    variation: InventoryVariation,
  ) => {
    const variationId = getVariationId(variation);
    if (!variationId) return;

    handleExpandedVariationStockChange(
      productId,
      variationId,
      variation.stock ?? 0,
    );
    setEditingVariationStockIds((prev) =>
      new Set(prev).add(
        getExpandedVariationStockEditKey(productId, variationId),
      ),
    );
  };

  const handleCancelExpandedVariationStockEdit = (
    productId: string,
    variation: InventoryVariation,
  ) => {
    const variationId = getVariationId(variation);
    if (!variationId) return;

    handleExpandedVariationStockChange(
      productId,
      variationId,
      variation.stock ?? 0,
    );
    setEditingVariationStockIds((prev) => {
      const next = new Set(prev);
      next.delete(getExpandedVariationStockEditKey(productId, variationId));
      return next;
    });
  };

  const handleUpdateProductAlert = async (
    item: InventoryItem,
    lowStockThreshold: number | null,
  ) => {
    const res = await BaseApi.patch(
      `/inventory/products/${item.id}/low-stock-threshold`,
      { lowStockThreshold },
    );

    if (!res?.success) {
      err(res?.message || res?.error || t("Failed to update stock alert"));
      throw new Error("update failed");
    }

    setInventory((prev) =>
      prev.map((inv) =>
        inv.id === item.id
          ? {
              ...inv,
              lowStockThreshold: res.data.lowStockThreshold,
              stockStatus: res.data.stockStatus,
              stockColor: res.data.stockColor,
            }
          : inv,
      ),
    );
    success(t("Stock alert updated"));
  };

  const handleUpdateVariationAlert = async (
    productId: string,
    variationId: string,
    lowStockThreshold: number | null,
  ) => {
    const res = await BaseApi.patch(
      `/inventory/variations/${variationId}/low-stock-threshold`,
      { lowStockThreshold },
    );

    if (!res?.success) {
      err(res?.message || res?.error || t("Failed to update stock alert"));
      throw new Error("update failed");
    }

    setExpandedVariations((prev) => ({
      ...prev,
      [productId]: (prev[productId] || []).map((variation) =>
        getVariationId(variation) === variationId
          ? { ...variation, lowStockThreshold: res.data.lowStockThreshold }
          : variation,
      ),
    }));
    success(t("Stock alert updated"));
  };

  const handleUpdateExpandedVariationStock = async (
    productId: string,
    variation: InventoryVariation,
  ) => {
    const variationId = getVariationId(variation);
    if (!variationId) return;

    const newStock =
      expandedVariationStockEdits[productId]?.get(variationId) ??
      variation.stock ??
      0;
    const currentStock = variation.stock ?? 0;
    const quantityChange = newStock - currentStock;

    if (quantityChange === 0) {
      return;
    }

    try {
      setSavingVariationId(variationId);
      const res = await BaseApi.post(`/inventory/${variationId}/adjust`, {
        quantity: Math.abs(quantityChange),
        type: quantityChange >= 0 ? "in" : "out",
        notes: "Manual adjustment",
      });

      if (res.success) {
        setExpandedVariations((prev) => ({
          ...prev,
          [productId]: (prev[productId] || []).map((item) =>
            getVariationId(item) === variationId
              ? { ...item, stock: newStock }
              : item,
          ),
        }));
        setExpandedVariationStockEdits((prev) => {
          const nextMap = new Map(prev[productId] || []);
          nextMap.set(variationId, newStock);
          return {
            ...prev,
            [productId]: nextMap,
          };
        });
        success(t("Variation stock updated successfully!"));
        setEditingVariationStockIds((prev) => {
          const next = new Set(prev);
          next.delete(getExpandedVariationStockEditKey(productId, variationId));
          return next;
        });
        fetchInventory();
      }
    } catch (error) {
      console.error("Error updating variation stock:", error);
      err(t("Failed to update variation stock"));
    } finally {
      setSavingVariationId(null);
    }
  };

  const handleEditStock = async (item: InventoryItem) => {
    // Fetch fresh details before editing
    const details = await fetchInventoryDetails(item.id);
    if (details) {
      setEditingItem(details);
    } else {
      setEditingItem(item);
    }
    // Fetch product variations
    setVariationsData([]);
    setVariationStockEdits(new Map());
    try {
      const variations = await fetchProductVariations(item.id);
      setVariationsData(variations);
      const editsMap = new Map<string, number>();
      variations.forEach((v: any) => {
        const vid = v.id || v._id;
        if (vid) editsMap.set(vid, v.stock ?? 0);
      });
      setVariationStockEdits(editsMap);
    } catch {
      // no variations or network error — proceed without them
    }
    setEditModal(true);
  };

  const handleUpdateVariationStocks = async () => {
    if (!editingItem) return;
    setSavingVariations(true);
    try {
      for (const variationId of Array.from(variationStockEdits.keys())) {
        const newStock = variationStockEdits.get(variationId) as number;
        const original = variationsData.find(
          (v) => (v.id || v._id) === variationId,
        );
        if (!original || (original.stock ?? 0) === newStock) continue;
        const quantityChange = newStock - (original.stock ?? 0);
        await BaseApi.post(`/inventory/${variationId}/adjust`, {
          quantity: Math.abs(quantityChange),
          // stock: newStock,
          type: quantityChange >= 0 ? "in" : "out",
          notes: "Manual adjustment",
        });
      }
      success(t("Variation stocks updated successfully!"));
      setEditModal(false);
      setEditingItem(null);
      setVariationsData([]);
      setVariationStockEdits(new Map());
      fetchInventory();
    } catch (error) {
      console.error("Error updating variation stocks:", error);
      err(t("Failed to update variation stocks"));
    } finally {
      setSavingVariations(false);
    }
  };

  const handleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
      const newUpdates = new Map(bulkUpdates);
      newUpdates.delete(itemId);
      setBulkUpdates(newUpdates);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleBulkStockChange = (itemId: string, newStock: number) => {
    const newUpdates = new Map(bulkUpdates);
    newUpdates.set(itemId, newStock);
    setBulkUpdates(newUpdates);
  };

  const openReviewModal = (
    approval: StockApprovalRequest,
    decision: "approved" | "rejected",
  ) => {
    if (!isAdmin) return;

    setSelectedApproval(approval);
    setApprovalDecision(decision);
    setApprovalNotes("");
    setReviewModal(true);
  };

  const closeReviewModal = () => {
    setReviewModal(false);
    setSelectedApproval(null);
    setApprovalDecision("approved");
    setApprovalNotes("");
  };

  const handleReviewStockApproval = async () => {
    if (!isAdmin || !selectedApproval?.id) return;

    try {
      setReviewingApproval(true);

      const fallbackNotes =
        approvalDecision === "approved"
          ? "Approved by admin"
          : "Rejected by admin";

      const res = await BaseApi.patch(
        `/inventory/stock-approvals/${selectedApproval.id}/review`,
        {
          decision: approvalDecision,
          notes: approvalNotes.trim() || fallbackNotes,
        },
      );

      if (res.success) {
        success(
          t(
            approvalDecision === "approved"
              ? "Stock approval request approved successfully"
              : "Stock approval request rejected successfully",
          ),
        );
        closeReviewModal();
        await fetchStockApprovals();
      }
    } catch (error) {
      console.error("Error reviewing stock approval:", error);
      err(t("Failed to review stock approval request"));
    } finally {
      setReviewingApproval(false);
    }
  };

  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const approvalsTotalPages = Math.ceil(
    approvalsTotalItems / approvalsItemsPerPage,
  );

  const getStockStatusColor = (status: string) => {
    return (
      stockStatuses.find((s) => s.value === status)?.color ||
      "bg-gray-100 text-gray-800"
    );
  };

  const getApprovalStatusColor = (status?: string) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const getApprovalStatusLabel = (status?: string) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return t("Approved");
      case "rejected":
        return t("Rejected");
      default:
        return t("Pending");
    }
  };

  const getApprovalProductName = (approval: StockApprovalRequest) => {
    const name = approval.product?.name || approval.productName;

    if (typeof name === "string") return name;

    return (
      name?.en ||
      name?.ar ||
      approval.product?.sku ||
      approval.sku ||
      t("Unknown Product")
    );
  };

  const getApprovalSku = (approval: StockApprovalRequest) =>
    approval.product?.sku || approval.sku || "-";

  const getUserDisplayName = (
    person?: {
      firstName?: string;
      lastName?: string;
      email?: string;
    } | null,
  ) => {
    const fullName = [person?.firstName, person?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || person?.email || "-";
  };

  const getRequestedStockValue = (approval: StockApprovalRequest) =>
    approval.requestedStock ??
    approval.proposedStock ??
    approval.newStock ??
    approval.stock;

  const getCurrentStockValue = (approval: StockApprovalRequest) =>
    approval.currentStock ?? approval.previousStock;

  const formatDateTime = (dateString?: string | null) =>
    dateString ? new Date(dateString).toLocaleString() : "-";

  const pageTabs = [
    {
      value: "inventory" as const,
      label: t("Inventory"),
    },
    {
      value: "stock_approvals" as const,
      label: isAdmin
        ? t("Stock Approval Requests")
        : t("My Stock Approval Requests"),
    },
  ];

  if (initialLoading) {
    return <LoadingComponent title={t("Inventory")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db]  p-4 sm:p-6 lg:p-8">
      <style>{`
            @keyframes slideDown {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div className="pt-4">
          <div className="flex flex-col gap-6 bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-5 min-w-0">
              <div className="relative">
                <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                  <StoreIcon className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-base sm:text-4xl font-bold gradient-text mb-1">
                  {t("Inventory")}
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">
                  {t("Track and manage stocks")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 w-full lg:w-auto lg:min-w-[540px] sm:grid-cols-2 xl:grid-cols-4">
              {headerActions.map((action) => {
                const Icon = action.icon;

                return (
                  <Tooltip key={action.label} title={action.label}>
                    <button
                      onClick={action.onClick}
                      disabled={action.disabled}
                      aria-label={action.label}
                      className={`flex w-full items-center justify-center rounded-xl transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm sm:font-semibold ${action.className} h-11 px-0 py-0 text-sm`}
                    >
                      <Icon size={18} />
                      <span className="hidden sm:inline">{action.label}</span>
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl my-4 p-3 sm:p-4 border border-gray-100">
          <div className="flex flex-wrap gap-2">
            {pageTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  activeTab === tab.value
                    ? "bg-black text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "inventory" && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-3xl shadow-2xl my-4 p-4 sm:p-8 border border-gray-100">
              <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm sm:text-base"
                    placeholder={t("Search by name or SKU...")}
                  />
                </div>

                <div className="relative w-full lg:w-64">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
                  <select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                    className="w-full appearance-none pl-10 pr-10 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors bg-white text-sm sm:text-base"
                  >
                    <option value="all">{t("All Status")}</option>
                    {stockStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    ▾
                  </span>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs sm:text-sm text-gray-600">
                  {t("Showing")} {filtered.length} {t("of")} {apiTotalItems}{" "}
                  {t("items")}
                </span>
                <Tooltip title={t("Refresh Inventory")}>
                  <button
                    onClick={fetchInventory}
                    className="self-start text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t("Refresh")}
                  </button>
                </Tooltip>
              </div>
            </div>

            {/* Inventory List */}
            <div
              className={
                "space-y-3 sm:space-y-4" +
                (loading
                  ? " opacity-50 pointer-events-none transition-opacity"
                  : "")
              }
              style={{ animation: "fadeIn 0.5s ease-out 0.2s backwards" }}
            >
              {filtered.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-16 text-center">
                  <StoreIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-base sm:text-lg">
                    {t("No items found")}
                  </p>
                </div>
              ) : (
                filtered.map((item) => {
                  const isExpanded = expandedItems.has(item.id);
                  const itemVariations = expandedVariations[item.id] || [];
                  const isLoadingVariations = loadingVariationItems.has(
                    item.id,
                  );

                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4"
                    >
                      <div className="flex items-start gap-3">
                        <Tooltip
                          title={
                            isExpanded
                              ? t("Hide Variations")
                              : t("Show Variations")
                          }
                        >
                          <button
                            type="button"
                            onClick={() => toggleVariationRows(item)}
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? t("Hide Variations")
                                : t("Show Variations")
                            }
                            className="mt-3 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200"
                          >
                            <ChevronDown
                              size={18}
                              className={`transition-transform ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        </Tooltip>

                        {/* Product Image */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                          {item?.images &&
                          Array.isArray(item.images) &&
                          item.images.length > 0 ? (
                            <img
                              src={item.images[0]}
                              alt={item.name?.en || item.name?.ar || item.sku}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement
                                  ?.querySelector(".fallback-icon")
                                  ?.classList.remove("hidden");
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <StoreIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                                {item.name?.en || item.name?.ar || item.sku}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-500">
                                {t("SKU")}: {item.sku}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${getStockStatusColor(
                                item.stockStatus,
                              )}`}
                            >
                              {
                                stockStatuses.find(
                                  (s) => s.value === item.stockStatus,
                                )?.label
                              }
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500">
                                {t("Stock")}:
                              </span>
                              <span className="ml-1 font-semibold">
                                {item.stock}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {t("Price")}:
                              </span>
                              <span className="ml-1 font-semibold">
                                <Price amount={Number(item.price)} />
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {t("Value")}:
                              </span>
                              <span className="ml-1 font-semibold">
                                <Price amount={Number(item.stockValue)} />
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {t("Alert")}:
                              </span>
                              <span className="ml-1">
                                <LowStockAlertEditor
                                  value={item.lowStockThreshold}
                                  onSave={(value) =>
                                    handleUpdateProductAlert(item, value)
                                  }
                                />
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditStock(item)}
                              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-700 transition-colors text-xs sm:text-sm font-semibold flex items-center justify-center gap-1"
                            >
                              <Edit2 size={14} />
                              {t("Update Stock")}
                            </button>
                            <button
                              onClick={() => handleSelectItem(item.id)}
                              className={`px-3 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm font-semibold ${
                                selectedItems.has(item.id)
                                  ? "bg-[#0d223f] text-white"
                                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                            >
                              {selectedItems.has(item.id)
                                ? t("Selected")
                                : t("Select")}
                            </button>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          {isLoadingVariations ? (
                            <div className="flex items-center justify-center py-6">
                              <div
                                className="h-6 w-6 rounded-full border-2 border-gray-400 border-t-transparent"
                                style={{ animation: "spin 1s linear infinite" }}
                              />
                            </div>
                          ) : itemVariations.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                              {t("No variations found")}
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <div className="min-w-[800px] rounded-lg border border-gray-200">
                                <div className="grid grid-cols-[minmax(150px,0.75fr)_minmax(160px,1fr)_190px_200px_128px] gap-3 rounded-t-lg bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-500">
                                  <div>{t("Variation")}</div>
                                  <div>{t("SKU")}</div>
                                  <div>{t("Stock")}</div>
                                  <div>{t("Alert")}</div>
                                  <div className="text-right">
                                    {t("Action")}
                                  </div>
                                </div>
                                {itemVariations.map((variation) => {
                                  const variationId = getVariationId(variation);
                                  if (!variationId) return null;

                                  const currentStock =
                                    expandedVariationStockEdits[item.id]?.get(
                                      variationId,
                                    ) ??
                                    variation.stock ??
                                    0;
                                  const originalStock = variation.stock ?? 0;
                                  const isChanged =
                                    currentStock !== originalStock;
                                  const isSaving =
                                    savingVariationId === variationId;
                                  const editKey =
                                    getExpandedVariationStockEditKey(
                                      item.id,
                                      variationId,
                                    );
                                  const isEditingStock =
                                    editingVariationStockIds.has(editKey);

                                  return (
                                    <div
                                      key={variationId}
                                      className="grid grid-cols-[minmax(150px,0.75fr)_minmax(160px,1fr)_190px_200px_128px] gap-3 border-t border-gray-200 px-3 py-3 text-sm"
                                    >
                                      <div className="min-w-0">
                                        <div className="break-words font-medium capitalize leading-5 text-gray-900">
                                          {formatVariationAttributes(
                                            variation.attributes,
                                          )}
                                        </div>
                                        {variation.price !== null &&
                                          variation.price !== undefined && (
                                            <div className="mt-1 text-xs text-gray-500">
                                              {t("Price")}:{" "}
                                              <Price
                                                amount={Number(
                                                  variation.price,
                                                )}
                                              />
                                            </div>
                                          )}
                                      </div>
                                      <div className="flex min-w-0 items-center break-all leading-5 text-gray-600">
                                        {variation.sku || "-"}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {isEditingStock ? (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleExpandedVariationStockChange(
                                                  item.id,
                                                  variationId,
                                                  currentStock - 1,
                                                )
                                              }
                                              disabled={isSaving}
                                              className="flex h-9 w-9 items-center justify-center rounded bg-gray-200 transition-colors hover:bg-gray-300 disabled:opacity-60"
                                            >
                                              <Minus size={14} />
                                            </button>
                                            <input
                                              type="number"
                                              min="0"
                                              value={currentStock}
                                              onChange={(e) =>
                                                handleExpandedVariationStockChange(
                                                  item.id,
                                                  variationId,
                                                  parseInt(e.target.value) || 0,
                                                )
                                              }
                                              disabled={isSaving}
                                              className="h-9 w-16 rounded border-2 border-gray-200 px-1 text-center text-sm font-semibold focus:border-black focus:outline-none disabled:bg-gray-100"
                                            />
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleExpandedVariationStockChange(
                                                  item.id,
                                                  variationId,
                                                  currentStock + 1,
                                                )
                                              }
                                              disabled={isSaving}
                                              className="flex h-9 w-9 items-center justify-center rounded bg-gray-200 transition-colors hover:bg-gray-300 disabled:opacity-60"
                                            >
                                              <Plus size={14} />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <span className="min-w-[44px] rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center font-semibold text-gray-900">
                                              {originalStock}
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleStartExpandedVariationStockEdit(
                                                  item.id,
                                                  variation,
                                                )
                                              }
                                              disabled={isSaving}
                                              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-gray-200 px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-300 disabled:opacity-60"
                                            >
                                              <Edit2 size={13} />
                                              {t("Edit")}
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      <div className="flex items-center text-sm">
                                        <LowStockAlertEditor
                                          value={
                                            variation.lowStockThreshold ?? null
                                          }
                                          inheritedValue={
                                            item.lowStockThreshold
                                          }
                                          allowReset
                                          onSave={(value) =>
                                            handleUpdateVariationAlert(
                                              item.id,
                                              variationId,
                                              value,
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="flex items-center justify-end">
                                        {isEditingStock ? (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleCancelExpandedVariationStockEdit(
                                                  item.id,
                                                  variation,
                                                )
                                              }
                                              disabled={isSaving}
                                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-60"
                                              aria-label={t("Cancel")}
                                            >
                                              <X size={14} />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleUpdateExpandedVariationStock(
                                                  item.id,
                                                  variation,
                                                )
                                              }
                                              disabled={!isChanged || isSaving}
                                              className="inline-flex h-9 min-w-[76px] items-center justify-center gap-1.5 rounded-lg bg-black px-3 text-xs font-semibold text-white transition-colors hover:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-600"
                                            >
                                              {isSaving ? (
                                                <div
                                                  className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                                                  style={{
                                                    animation:
                                                      "spin 1s linear infinite",
                                                  }}
                                                />
                                              ) : (
                                                <Save size={14} />
                                              )}
                                              {isSaving
                                                ? t("Saving...")
                                                : t("Save")}
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-400">
                                            -
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {activeTab === "stock_approvals" && (
          <div className="bg-white rounded-3xl shadow-2xl my-4 p-4 sm:p-8 border border-gray-100">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {isAdmin
                    ? t("Stock Approval Requests")
                    : t("My Stock Approval Requests")}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isAdmin
                    ? t(
                        "Review pending stock approval requests from your team.",
                      )
                    : t("Track the stock approval requests you submitted.")}
                </p>
              </div>
              <button
                onClick={fetchStockApprovals}
                disabled={approvalsLoading}
                className="self-start text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
              >
                {approvalsLoading ? t("Refreshing...") : t("Refresh")}
              </button>
            </div>

            <div className="mt-6">
              {approvalsLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div
                    className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full"
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                </div>
              ) : stockApprovals.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
                  <p className="text-gray-500">
                    {isAdmin
                      ? t("No stock approval requests found")
                      : t("You have not made any stock approval requests yet")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stockApprovals.map((approval) => {
                    const status = String(approval.status || "").toLowerCase();
                    const requestedStock = getRequestedStockValue(approval);
                    const currentStock = getCurrentStockValue(approval);

                    return (
                      <div
                        key={approval.id}
                        className="rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-5"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base font-semibold text-gray-900">
                                {getApprovalProductName(approval)}
                              </h3>
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getApprovalStatusColor(
                                  approval.status,
                                )}`}
                              >
                                {getApprovalStatusLabel(approval.status)}
                              </span>
                            </div>

                            <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-gray-600 sm:grid-cols-2 xl:grid-cols-4">
                              <div>
                                <span className="font-medium text-gray-800">
                                  {t("SKU")}:
                                </span>{" "}
                                {getApprovalSku(approval)}
                              </div>
                              <div>
                                <span className="font-medium text-gray-800">
                                  {t("Current Stock")}:
                                </span>{" "}
                                {currentStock ?? "-"}
                              </div>
                              <div>
                                <span className="font-medium text-gray-800">
                                  {t("Requested Stock")}:
                                </span>{" "}
                                {requestedStock ?? "-"}
                              </div>
                              <div>
                                <span className="font-medium text-gray-800">
                                  {t("Requested At")}:
                                </span>{" "}
                                {formatDateTime(approval.createdAt)}
                              </div>
                            </div>

                            <div className="mt-3 space-y-1 text-sm text-gray-600">
                              <div>
                                <span className="font-medium text-gray-800">
                                  {t("Requested By")}:
                                </span>{" "}
                                {getUserDisplayName(approval.requester)}
                              </div>
                              {approval.reviewer && (
                                <div>
                                  <span className="font-medium text-gray-800">
                                    {t("Reviewed By")}:
                                  </span>{" "}
                                  {getUserDisplayName(approval.reviewer)}
                                </div>
                              )}
                              {approval.notes && (
                                <div>
                                  <span className="font-medium text-gray-800">
                                    {t("Request Notes")}:
                                  </span>{" "}
                                  {approval.notes}
                                </div>
                              )}
                              {approval.reviewNotes && (
                                <div>
                                  <span className="font-medium text-gray-800">
                                    {t("Review Notes")}:
                                  </span>{" "}
                                  {approval.reviewNotes}
                                </div>
                              )}
                              {approval.reviewedAt && (
                                <div>
                                  <span className="font-medium text-gray-800">
                                    {t("Reviewed At")}:
                                  </span>{" "}
                                  {formatDateTime(approval.reviewedAt)}
                                </div>
                              )}
                            </div>
                          </div>

                          {isAdmin && status === "pending" && (
                            <div className="flex flex-wrap gap-2 lg:justify-end">
                              <button
                                onClick={() =>
                                  openReviewModal(approval, "approved")
                                }
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-semibold"
                              >
                                <CheckCircle size={16} />
                                {t("Approve")}
                              </button>
                              <button
                                onClick={() =>
                                  openReviewModal(approval, "rejected")
                                }
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-semibold"
                              >
                                <XCircle size={16} />
                                {t("Reject")}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {approvalsTotalItems > 0 && (
              <Pagination
                currentPage={approvalsCurrentPage}
                totalPages={approvalsTotalPages}
                totalItems={approvalsTotalItems}
                itemsPerPage={approvalsItemsPerPage}
                onPageChange={setApprovalsCurrentPage}
                onPageSizeChange={(size) => {
                  setApprovalsItemsPerPage(size);
                  setApprovalsCurrentPage(1);
                }}
                pageSizeOptions={[5, 10, 20, 50]}
                showPageSizeSelector={true}
                isLoading={approvalsLoading}
                compact={false}
              />
            )}
          </div>
        )}
      </div>

      {/* Edit Stock Modal */}
      {editModal && editingItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setEditModal(false);
            setEditingItem(null);
            setVariationsData([]);
            setVariationStockEdits(new Map());
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                {t("Update Stock")}
              </h3>
              <button
                onClick={() => {
                  setEditModal(false);
                  setEditingItem(null);
                }}
                className="text-white hover:text-gray-300"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              {/* <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gray-200 flex-shrink-0 overflow-hidden">
                  {editingItem.images && editingItem.images.length > 0 ? (
                    <img
                      src={editingItem.images[0]}
                      alt={
                        editingItem.name?.en ||
                        editingItem.name?.ar ||
                        editingItem.sku
                      }
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <StoreIcon className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                    {editingItem.name?.en || editingItem.name?.ar}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    {t("SKU")}: {editingItem.sku}
                  </div>
                </div>
              </div> */}

              {/* Variation stock editor */}
              {variationsData.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">
                      {t("Variation Stock")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {variationsData.length} {t("variations")}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {variationsData.map((v) => {
                      const vid = v.id || v._id;
                      const currentStock =
                        variationStockEdits.get(vid) ?? v.stock ?? 0;
                      const attrLabel = Object.entries(v.attributes || {})
                        .map(([k, val]) => `${k}: ${val}`)
                        .join(" · ");
                      return (
                        <div
                          key={vid}
                          className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-800 truncate capitalize">
                              {attrLabel}
                            </div>
                            <div className="text-xs text-gray-400">
                              SKU: {v.sku}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() =>
                                setVariationStockEdits((prev) => {
                                  const m = new Map(prev);
                                  m.set(vid, Math.max(0, currentStock - 1));
                                  return m;
                                })
                              }
                              className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                            >
                              <Minus size={12} />
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={currentStock}
                              onChange={(e) =>
                                setVariationStockEdits((prev) => {
                                  const m = new Map(prev);
                                  m.set(vid, parseInt(e.target.value) || 0);
                                  return m;
                                })
                              }
                              className="w-16 px-1 py-1.5 text-center text-sm font-semibold rounded border-2 border-gray-200 focus:border-black focus:outline-none"
                            />
                            <button
                              onClick={() =>
                                setVariationStockEdits((prev) => {
                                  const m = new Map(prev);
                                  m.set(vid, currentStock + 1);
                                  return m;
                                })
                              }
                              className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* Product-level stock editor (no variations) */
                <>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">
                      {t("Current Stock")}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {editingItem.stock} {t("units")}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("New Stock Quantity")}
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setEditingItem({
                            ...editingItem,
                            stock: Math.max(0, editingItem.stock - 1),
                          })
                        }
                        className="p-3 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                      >
                        <Minus size={18} />
                      </button>
                      <input
                        type="number"
                        min="0"
                        value={editingItem.stock}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            stock: parseInt(e.target.value) || 0,
                          })
                        }
                        className="flex-1 px-4 py-3 text-center text-lg font-semibold rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                      />
                      <button
                        onClick={() =>
                          setEditingItem({
                            ...editingItem,
                            stock: editingItem.stock + 1,
                          })
                        }
                        className="p-3 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>

                  {editingItem.stock <= editingItem.lowStockThreshold && (
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <strong>{t("Low Stock Warning")}:</strong>{" "}
                        {t("This quantity is at or below the threshold of")}{" "}
                        {editingItem.lowStockThreshold} {t("units.")}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setEditModal(false);
                  setEditingItem(null);
                  setVariationsData([]);
                  setVariationStockEdits(new Map());
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 font-semibold text-sm sm:text-base"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() =>
                  variationsData.length > 0
                    ? handleUpdateVariationStocks()
                    : handleUpdateStock(editingItem!.id, editingItem!.stock)
                }
                disabled={saving || savingVariations}
                className="flex-1 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-70 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {saving || savingVariations ? (
                  <>
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Saving...")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("Save")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {bulkModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setBulkModal(false);
            setSelectedItems(new Set());
            setBulkUpdates(new Map());
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  {t("Bulk Update Stock")}
                </h3>
                <p className="text-sm text-gray-300">
                  {selectedItems.size} {t("items selected")}
                </p>
              </div>
              <button
                onClick={() => {
                  setBulkModal(false);
                  setSelectedItems(new Set());
                  setBulkUpdates(new Map());
                }}
                className="text-white hover:text-gray-300"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-3">
              {selectedItems.size === 0 ? (
                <div className="text-center py-8">
                  <StoreIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t("No items selected")}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {t("Select items from the list to bulk update")}
                  </p>
                </div>
              ) : (
                Array.from(selectedItems).map((itemId) => {
                  const item = inventory.find((i) => i.id === itemId);
                  if (!item) return null;

                  const newStock = bulkUpdates.get(itemId) ?? item.stock;

                  return (
                    <div
                      key={itemId}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0 overflow-hidden">
                        {item?.images &&
                        Array.isArray(item.images) &&
                        item.images.length > 0 ? (
                          <img
                            src={item.images[0]}
                            alt={item.name?.en || item.sku}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement
                                ?.querySelector(".fallback-icon")
                                ?.classList.remove("hidden");
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <StoreIcon className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">
                          {item.name?.en || item.name?.ar || item.sku}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t("Current")}: {item.stock}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            handleBulkStockChange(
                              itemId,
                              Math.max(0, newStock - 1),
                            )
                          }
                          className="p-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                        >
                          <Minus size={14} />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={newStock}
                          onChange={(e) =>
                            handleBulkStockChange(
                              itemId,
                              parseInt(e.target.value) || 0,
                            )
                          }
                          className="w-16 px-2 py-1 text-center text-sm font-semibold rounded border-2 border-gray-200 focus:border-black focus:outline-none"
                        />
                        <button
                          onClick={() =>
                            handleBulkStockChange(itemId, newStock + 1)
                          }
                          className="p-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {selectedItems.size > 0 && (
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex gap-2 sm:gap-3 border-t pt-4">
                <button
                  onClick={() => {
                    setBulkModal(false);
                    setSelectedItems(new Set());
                    setBulkUpdates(new Map());
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 font-semibold text-sm sm:text-base"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleBulkUpdate}
                  disabled={saving || bulkUpdates.size === 0}
                  className="flex-1 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-70 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {saving ? (
                    <>
                      <div
                        className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span>{t("Updating...")}</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                      {t("Update")} {bulkUpdates.size} {t("Items")}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transactions Modal */}
      {transactionsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setTransactionsModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-white" />
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  {t("Inventory Transactions")}
                </h3>
              </div>
              <button
                onClick={() => setTransactionsModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div
              ref={transactionsScrollRef}
              className="p-4 sm:p-6 max-h-[calc(90vh-88px)] overflow-y-auto"
            >
              {transactionsInitialLoading ? (
                <div className="py-10">
                  <LoadingComponent title={t("Inventory Transactions")} />
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t("No transactions found")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
                        {/* Product Info */}
                        <div className="flex gap-3">
                          <img
                            src={
                              transaction.product?.images?.[0] ||
                              "/placeholder.png"
                            }
                            alt={
                              transaction.product?.name?.en || "Product image"
                            }
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <div className="flex flex-col justify-between text-sm space-y-1">
                            <h4 className="font-semibold text-gray-900">
                              {transaction.product?.name?.en ||
                                transaction.product?.name?.ar ||
                                transaction.productId}
                            </h4>
                            <p className="text-gray-700">
                              SKU: {transaction.product?.sku || "-"}
                            </p>
                            {transaction.reason && (
                              <p className="text-gray-600">
                                Reason: {transaction.reason}
                              </p>
                            )}
                            {transaction.notes && (
                              <p className="text-gray-600">
                                Notes: {transaction.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Transaction Info */}
                        <div className="flex justify-between gap-4 text-sm">
                          <div className="space-y-1 text-gray-600">
                            <div>
                              Stock: {transaction.previousStock} →{" "}
                              {transaction.newStock} {t("units")}
                            </div>
                            {transaction.createdAt && (
                              <div>
                                Created:{" "}
                                {new Date(
                                  transaction.createdAt,
                                ).toLocaleString()}
                              </div>
                            )}
                            {transaction.updatedAt && (
                              <div>
                                Updated:{" "}
                                {new Date(
                                  transaction.updatedAt,
                                ).toLocaleString()}
                              </div>
                            )}
                            {transaction.deletedAt && (
                              <div>
                                Deleted:{" "}
                                {new Date(
                                  transaction.deletedAt,
                                ).toLocaleString()}
                              </div>
                            )}
                          </div>

                          <div
                            className={`px-3 py-1 rounded-full font-semibold self-start ${
                              transaction.type === "in"
                                ? "bg-green-100 text-green-800"
                                : transaction.type === "out"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {transaction.type?.toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {transactionsHasMore && (
                    <div
                      ref={transactionsObserverRef}
                      className="flex items-center justify-center py-4"
                    >
                      {transactionsLoading && (
                        <div
                          className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full"
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                      )}
                    </div>
                  )}
                  {!transactionsHasMore && transactions.length > 0 && (
                    <div className="py-3 text-center text-sm text-gray-500">
                      {t("No more transactions")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {reviewModal && selectedApproval && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeReviewModal}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                {approvalDecision === "approved"
                  ? t("Approve Stock Request")
                  : t("Reject Stock Request")}
              </h3>
              <button
                onClick={closeReviewModal}
                className="text-white hover:text-gray-300"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                <div className="text-sm text-gray-500">{t("Product")}</div>
                <div className="font-semibold text-gray-900">
                  {getApprovalProductName(selectedApproval)}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {t("Current Stock")}:{" "}
                  {getCurrentStockValue(selectedApproval) ?? "-"}
                </div>
                <div className="text-sm text-gray-600">
                  {t("Requested Stock")}:{" "}
                  {getRequestedStockValue(selectedApproval) ?? "-"}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Notes")}
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={4}
                  placeholder={
                    approvalDecision === "approved"
                      ? t("Approved by admin")
                      : t("Rejected by admin")
                  }
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-black focus:outline-none resize-none"
                />
              </div>
            </div>

            <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex gap-3">
              <button
                onClick={closeReviewModal}
                disabled={reviewingApproval}
                className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 font-semibold text-sm sm:text-base disabled:opacity-70"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleReviewStockApproval}
                disabled={reviewingApproval}
                className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base text-white disabled:opacity-70 flex items-center justify-center gap-2 ${
                  approvalDecision === "approved"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {reviewingApproval ? (
                  <>
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Submitting...")}</span>
                  </>
                ) : (
                  <>
                    {approvalDecision === "approved" ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                    <span>
                      {approvalDecision === "approved"
                        ? t("Approve")
                        : t("Reject")}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination indicators */}
      {activeTab === "inventory" && finalTotalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={finalTotalPages}
          totalItems={finalTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          showPageSizeSelector={true}
          isLoading={loading}
          compact={true}
        />
      )}
    </div>
  );
};
export default InventoryManagementPage;
